import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { clearCache, getCachedData } from '../services/cacheService';
import CacheStatus from './CacheStatus';
import { Users, BarChart3, MessageCircleOff, LogOut, RefreshCw } from 'lucide-react';
import { Button, Modal } from './ui';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, signOut } = useAdminAuth();
  const location = useLocation();
  const [cacheTimestamp, setCacheTimestamp] = useState<Date | null>(null);
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);

  useEffect(() => {
    const updateCacheTimestamp = async () => {
      const cached = await getCachedData('all_users');
      if (cached) {
        setCacheTimestamp(cached.lastRefreshed);
      }
    };
    updateCacheTimestamp();
    const interval = setInterval(updateCacheTimestamp, 60000);
    const onCacheUpdated = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === 'all_users') updateCacheTimestamp();
    };
    window.addEventListener('admin:cacheUpdated', onCacheUpdated);
    return () => {
      clearInterval(interval);
      window.removeEventListener('admin:cacheUpdated', onCacheUpdated);
    };
  }, []);

  const handlePurgeCacheClick = () => {
    setPurgeModalOpen(true);
  };

  const handlePurgeConfirm = async () => {
    await clearCache();
    setCacheTimestamp(null);
    window.location.reload();
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-neutral-border/20">
      {/* Header */}
      <header className="border-b border-neutral-border bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between items-center">
            <h1 className="text-xl font-bold text-brand-primary">Gantty Admin</h1>
            <div className="flex items-center gap-4">
              <CacheStatus lastRefreshed={cacheTimestamp} />
              <Button
                variant="secondary"
                onClick={handlePurgeCacheClick}
                title="Purge cache and reload"
                className="h-9 gap-2 px-3"
              >
                <RefreshCw className="h-4 w-4" />
                Purge Cache & Reload
              </Button>
              <span className="text-sm text-neutral-secondary">{user?.email}</span>
              <Button variant="ghost" onClick={handleSignOut} className="h-9 gap-2 px-3 text-red-600 hover:bg-red-50 hover:text-red-700">
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="min-h-[calc(100vh-4rem)] w-64 border-r border-neutral-border bg-white">
          <nav className="space-y-1 p-4">
            <Link
              to="/users"
              className={`flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === '/users' || location.pathname === '/'
                  ? 'bg-brand-primary text-white'
                  : 'text-neutral-primary hover:bg-neutral-border/50'
              }`}
            >
              <Users className="h-5 w-5" />
              Users
            </Link>
            <Link
              to="/usage"
              className={`flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === '/usage'
                  ? 'bg-brand-primary text-white'
                  : 'text-neutral-primary hover:bg-neutral-border/50'
              }`}
            >
              <BarChart3 className="h-5 w-5" />
              Usage Stats
            </Link>
            <Link
              to="/unsupported-requests"
              className={`flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === '/unsupported-requests'
                  ? 'bg-brand-primary text-white'
                  : 'text-neutral-primary hover:bg-neutral-border/50'
              }`}
            >
              <MessageCircleOff className="h-5 w-5" />
              Unsupported Requests
            </Link>
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
      <Modal
        open={purgeModalOpen}
        onClose={() => setPurgeModalOpen(false)}
        message="Are you sure you want to purge all cache and reload data?"
        variant="confirm"
        confirmLabel="Purge and reload"
        onConfirm={handlePurgeConfirm}
      />
    </div>
  );
}
